import { useState, useRef, useCallback, useEffect, memo } from 'react';
import styled from 'styled-components';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { useTheme } from '../context/ThemeContext';
import { Avatar } from './';
import { getTaskPriorityLabel, getTaskTypeLabel } from '../api';
import { StatusPalette, SupportTaskStatusColors, SupportTaskPriorityColors, SupportTaskTypeColors } from '../constants/statusColors';
import { ChevronLeft, ChevronRight, ArrowUpToLine, Inbox } from 'lucide-react';

const STATUS_COLUMNS = [
  { key: 'Backlog', label: 'Backlog', color: SupportTaskStatusColors.Backlog, collapsible: true },
  { key: 'Open', label: 'Open', color: SupportTaskStatusColors.Open },
  { key: 'InProgress', label: 'In Progress', color: SupportTaskStatusColors.InProgress },
  { key: 'OnHold', label: 'On Hold', color: SupportTaskStatusColors.OnHold },
  { key: 'Resolved', label: 'Resolved', color: SupportTaskStatusColors.Resolved },
  { key: 'Closed', label: 'Closed', color: SupportTaskStatusColors.Closed, collapsible: true },
];

const COLLAPSED_STORAGE_KEY = 'kanban_collapsedColumns';

const getInitialCollapsed = () => {
  try {
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {}
  return new Set(['Closed', 'Backlog']);
};

// ── Styled Components ──

const KanbanContainer = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 4px 2px ${props => props.theme.spacing.md} 2px;
  min-height: 500px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
`;

const Column = styled.div`
  min-width: 260px;
  max-width: 320px;
  flex: 1 1 260px;
  display: flex;
  flex-direction: column;
  background-color: ${props => `${props.$color}06`};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  transition: border-color 0.2s ease;
`;

const ColumnHeader = styled.div`
  padding: 12px ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 2px solid ${props => props.$color};
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 13px;
  color: ${props => props.theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const ColumnDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  flex-shrink: 0;
`;

const ColumnCount = styled.span`
  background-color: ${props => `${props.$color}18`};
  color: ${props => props.$color};
  font-size: 12px;
  font-weight: 700;
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 11px;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
  }
`;

const ColumnBody = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 120px;
  overflow-y: auto;
  max-height: calc(100vh - 280px);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 2px;
  }
`;

const TaskCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-left: 3px solid ${props => props.$priorityColor || props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 10px 12px;
  cursor: grab;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  user-select: none;
  position: relative;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
    border-color: ${props => props.theme.colors.border};
    border-left-color: ${props => props.$priorityColor || props.theme.colors.border};
  }

  &:active {
    cursor: grabbing;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const MoveToTopButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  width: 22px;
  height: 22px;
  padding: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.tint};
    border-color: ${props => props.theme.colors.tint};
    background: ${props => `${props.theme.colors.tint}08`};
  }

  ${TaskCard}:hover & {
    display: flex;
  }
`;

const TaskCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const TaskNumber = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.tint};
  font-weight: 600;
  font-family: monospace;
  letter-spacing: -0.3px;
`;

const PriorityBadge = styled.span`
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background-color: ${props => `${props.$color}15`};
  color: ${props => props.$color};
  white-space: nowrap;
`;

const TaskTitle = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 8px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TaskCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
`;

const TypeBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => `${props.$color}12`};
  color: ${props => props.$color};
  white-space: nowrap;
`;

const DueDate = styled.span`
  font-size: 11px;
  font-weight: ${props => props.$overdue ? '600' : '500'};
  color: ${props => props.$overdue ? StatusPalette.red : props.theme.colors.muted};
  white-space: nowrap;
  ${props => props.$overdue && `
    background: ${StatusPalette.red}10;
    padding: 1px 5px;
    border-radius: 3px;
  `}
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EmptyColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  gap: 8px;
  flex: 1;
  opacity: 0.6;
`;

const EmptyIcon = styled.div`
  color: ${props => props.theme.colors.border};
`;

// ── Collapsed Column ──

const CollapsedColumn = styled.div`
  width: 40px;
  min-width: 40px;
  flex-shrink: 0;
  position: sticky;
  ${props => props.$position === 'start' ? 'left: 0;' : 'right: 0;'}
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  cursor: ${props => props.$empty ? 'default' : 'pointer'};
  opacity: ${props => props.$empty ? 0.4 : 1};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.$empty ? props.theme.colors.border : `${props.$color}80`};
    background-color: ${props => props.$empty ? 'transparent' : `${props.$color}06`};
  }
`;

const CollapsedHeader = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${props => props.$color};
  flex-shrink: 0;
`;

const CollapsedLabel = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.muted};
  padding: ${props => props.theme.spacing.md} 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const CollapsedCount = styled.span`
  background-color: ${props => `${props.$color}18`};
  color: ${props => props.$color};
  font-size: 10px;
  font-weight: 700;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  margin-bottom: 8px;
`;

const CollapsedExpandIcon = styled.div`
  color: ${props => props.theme.colors.muted};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

// ── Helpers ──

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = d - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return { text: `${Math.abs(days)}d overdue`, overdue: true };
    if (days === 0) return { text: 'Today', overdue: false };
    if (days === 1) return { text: 'Tomorrow', overdue: false };
    return { text: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), overdue: false };
  } catch {
    return null;
  }
};

// ── KanbanColumn ──

const KanbanColumn = memo(({ columnKey, label, color, collapsible, tasks: initialTasks, isCollapsed, reorderEnabled, onToggleCollapse, onReorder, onStatusChange, onTaskClick, onMoveToTop, draggedTaskRef }) => {
  const theme = useTheme();
  const prevTasksRef = useRef(JSON.stringify(initialTasks.map(t => t.id)));
  const debounceRef = useRef(null);
  const suppressSyncRef = useRef(false);
  // Baseline tracks the last committed order — updates after each reorder fires,
  // so rapid successive drags each compare against the post-previous-drag state.
  const baselineRef = useRef(initialTasks.map(t => t.id));

  const [listRef, items, setItems] = useDragAndDrop(initialTasks, {
    group: reorderEnabled ? 'kanban-tasks' : undefined,
  });

  // Sync parent tasks -> internal items when parent data changes
  useEffect(() => {
    if (suppressSyncRef.current) return;
    const newIds = JSON.stringify(initialTasks.map(t => t.id));
    if (newIds !== prevTasksRef.current) {
      prevTasksRef.current = newIds;
      baselineRef.current = initialTasks.map(t => t.id);
      setItems(initialTasks);
    }
  }, [initialTasks, setItems]);

  // Debounced reorder detection
  useEffect(() => {
    const currentIds = items.map(t => t.id);
    const currentStr = JSON.stringify(currentIds);
    const baselineStr = JSON.stringify(baselineRef.current);

    if (currentStr === baselineStr) return;

    suppressSyncRef.current = true;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      suppressSyncRef.current = false;

      const baseline = baselineRef.current;
      const baselineIds = new Set(baseline);

      // Check for incoming task (cross-column drop)
      for (let i = 0; i < currentIds.length; i++) {
        if (!baselineIds.has(currentIds[i])) {
          baselineRef.current = currentIds;
          if (onReorder) {
            onReorder(currentIds[i], columnKey, i);
          } else if (onStatusChange) {
            onStatusChange(currentIds[i], columnKey);
          }
          return;
        }
      }

      // Within-column reorder
      if (reorderEnabled && onReorder) {
        for (let i = 0; i < currentIds.length; i++) {
          if (currentIds[i] !== baseline[i]) {
            baselineRef.current = currentIds;
            onReorder(currentIds[i], columnKey, i);
            return;
          }
        }
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [items, columnKey, reorderEnabled, onReorder, onStatusChange]);

  if (isCollapsed) return null;

  return (
    <Column theme={theme} $color={color}>
      <ColumnHeader theme={theme} $color={color}>
        <ColumnTitle theme={theme}>
          <ColumnDot $color={color} />
          {label}
        </ColumnTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ColumnCount theme={theme} $color={color}>{items.length}</ColumnCount>
          {collapsible && (
            <CollapseButton theme={theme} onClick={() => onToggleCollapse(columnKey)} title="Collapse column">
              <ChevronLeft size={14} />
            </CollapseButton>
          )}
        </div>
      </ColumnHeader>
      <ColumnBody
        ref={listRef}
        theme={theme}
        onDragStartCapture={(e) => {
          const card = e.target.closest('[data-task-id]');
          if (card && draggedTaskRef) {
            draggedTaskRef.current = parseInt(card.dataset.taskId, 10);
          }
        }}
        onDragEndCapture={() => {
          if (draggedTaskRef) draggedTaskRef.current = null;
        }}
      >
        {items.length === 0 ? (
          <EmptyColumn theme={theme}>
            <EmptyIcon theme={theme}><Inbox size={24} /></EmptyIcon>
            No tasks
          </EmptyColumn>
        ) : (
          items.map((task, index) => {
            const due = formatDate(task.dueDate);
            const priorityColor = SupportTaskPriorityColors[task.priority] || '#64748b';
            return (
              <TaskCard
                key={task.id}
                theme={theme}
                data-task-id={task.id}
                $priorityColor={priorityColor}
                onClick={() => onTaskClick?.(task.id)}
              >
                {reorderEnabled && index > 0 && onMoveToTop && (
                  <MoveToTopButton
                    theme={theme}
                    title="Move to top"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToTop(task.id, columnKey);
                    }}
                  >
                    <ArrowUpToLine size={12} />
                  </MoveToTopButton>
                )}
                <TaskCardHeader theme={theme}>
                  <TaskNumber theme={theme}>{task.taskNumber || `#${task.id}`}</TaskNumber>
                  <PriorityBadge $color={priorityColor}>
                    {getTaskPriorityLabel(task.priority)}
                  </PriorityBadge>
                </TaskCardHeader>
                <TaskTitle theme={theme}>{task.title}</TaskTitle>
                <TaskCardFooter theme={theme}>
                  <BadgeRow>
                    <TypeBadge $color={SupportTaskTypeColors[task.taskType] || '#64748b'}>
                      {getTaskTypeLabel(task.taskType)}
                    </TypeBadge>
                  </BadgeRow>
                  <MetaRow>
                    {due && (
                      <DueDate theme={theme} $overdue={due.overdue}>
                        {due.text}
                      </DueDate>
                    )}
                    {task.assignedToUserName && (
                      <Avatar name={task.assignedToUserName} size="xs" />
                    )}
                  </MetaRow>
                </TaskCardFooter>
              </TaskCard>
            );
          })
        )}
      </ColumnBody>
    </Column>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

// ── KanbanBoard ──

export const KanbanBoard = ({ tasks = [], onStatusChange, onReorder, onTaskClick, sortBy = 'custom' }) => {
  const theme = useTheme();
  const [collapsedColumns, setCollapsedColumns] = useState(getInitialCollapsed);
  const dragCounter = useRef({});
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const draggedTaskRef = useRef(null);

  const reorderEnabled = sortBy === 'custom';

  const toggleCollapse = useCallback((key) => {
    setCollapsedColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const sortColumnTasks = useCallback((columnTasks) => {
    if (sortBy === 'custom' || sortBy === 'status') {
      return columnTasks;
    }
    const sorted = [...columnTasks];
    if (sortBy === 'priority') {
      const priorityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };
      sorted.sort((a, b) => {
        const diff = (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99);
        if (diff !== 0) return diff;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    } else if (sortBy === 'dueDate') {
      sorted.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
    } else if (sortBy === 'created') {
      sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return sorted;
  }, [sortBy]);

  const columnData = STATUS_COLUMNS.map(col => ({
    ...col,
    tasks: sortColumnTasks(tasks.filter(t => t.status === col.key)),
  }));

  const handleCollapsedDragEnter = (e, columnKey) => {
    e.preventDefault();
    dragCounter.current[columnKey] = (dragCounter.current[columnKey] || 0) + 1;
    setDragOverColumn(columnKey);
  };

  const handleCollapsedDragLeave = (e, columnKey) => {
    dragCounter.current[columnKey] = (dragCounter.current[columnKey] || 0) - 1;
    if (dragCounter.current[columnKey] <= 0) {
      dragCounter.current[columnKey] = 0;
      if (dragOverColumn === columnKey) setDragOverColumn(null);
    }
  };

  const handleCollapsedDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCollapsedDrop = (e, columnKey) => {
    e.preventDefault();
    dragCounter.current[columnKey] = 0;
    setDragOverColumn(null);

    const taskId = draggedTaskRef.current;
    draggedTaskRef.current = null;

    if (taskId) {
      if (onReorder) {
        onReorder(taskId, columnKey, 0, { immediate: true });
      } else if (onStatusChange) {
        onStatusChange(taskId, columnKey);
      }
    }

    if (collapsedColumns.has(columnKey)) {
      toggleCollapse(columnKey);
    }
  };

  const handleMoveToTop = useCallback((taskId, status) => {
    if (onReorder) {
      onReorder(taskId, status, 0);
    }
  }, [onReorder]);

  return (
    <KanbanContainer theme={theme}>
      {columnData.map(col => {
        const isEmpty = col.tasks.length === 0;
        const isCollapsed = col.collapsible && (collapsedColumns.has(col.key) || isEmpty);

        if (isCollapsed) {
          return (
            <CollapsedColumn
              key={col.key}
              theme={theme}
              $color={col.color}
              $empty={isEmpty}
              $position={col.key === 'Backlog' ? 'start' : 'end'}
              onClick={() => !isEmpty && toggleCollapse(col.key)}
              onDragEnter={(e) => handleCollapsedDragEnter(e, col.key)}
              onDragLeave={(e) => handleCollapsedDragLeave(e, col.key)}
              onDragOver={handleCollapsedDragOver}
              onDrop={(e) => handleCollapsedDrop(e, col.key)}
              style={dragOverColumn === col.key ? { borderColor: `${col.color}80`, backgroundColor: `${col.color}06` } : undefined}
            >
              <CollapsedHeader $color={col.color} />
              <CollapsedLabel theme={theme}>{col.label}</CollapsedLabel>
              <CollapsedCount theme={theme} $color={col.color}>{col.tasks.length}</CollapsedCount>
              {!isEmpty && (
                <CollapsedExpandIcon theme={theme}>
                  <ChevronRight size={14} />
                </CollapsedExpandIcon>
              )}
            </CollapsedColumn>
          );
        }

        return (
          <KanbanColumn
            key={col.key}
            columnKey={col.key}
            label={col.label}
            color={col.color}
            collapsible={col.collapsible}
            tasks={col.tasks}
            isCollapsed={false}
            reorderEnabled={reorderEnabled}
            onToggleCollapse={toggleCollapse}
            onReorder={onReorder}
            onStatusChange={onStatusChange}
            onTaskClick={onTaskClick}
            onMoveToTop={handleMoveToTop}
            draggedTaskRef={draggedTaskRef}
          />
        );
      })}
    </KanbanContainer>
  );
};

export default KanbanBoard;
